import React, { useState, useEffect } from "react";
import { 
  Row, Col, Card, Progress, Button, Tag, Typography, Space, 
  Divider, Checkbox, Tooltip, Badge, Empty, message, List, Popover, Image, notification
} from "antd";
import { 
  FileWordFilled, 
  DownloadOutlined, 
  CheckCircleFilled, 
  ExclamationCircleOutlined,
  HighlightOutlined,
  EyeOutlined,
  EditOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  CloseOutlined
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import SignatureModal from "./SignatureModal";
import { getSignatureLabel } from "./utils/signatureConfig";

const { Title, Text } = Typography;

const CARD = {
  background: "#fff",
  border: "0.5px solid rgba(0,0,0,0.08)",
  borderRadius: "12px",
};

export default function SleekTemplateView({ id, verification, visits, templateFields, loading: parentLoading, refresh }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeSignature, setActiveSignature] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVisits, setSelectedVisits] = useState({}); // { docId: [visitId1, visitId2] }

  const showMissingFields = (doc) => {
    const key = `open${Date.now()}`;
    notification.warning({
      message: `Campos Faltantes: ${doc.title}`,
      description: (
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {doc.mGen.length > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <Text strong style={{ fontSize: "12px" }}>Generales:</Text><br/>
              {doc.mGen.map(k => <Tag key={k} style={{margin: "2px", fontSize: "11px"}}>{k}</Tag>)}
            </div>
          )}
          {doc.mVisits.map(v => (
            <div key={v.label} style={{ marginBottom: "8px" }}>
              <Text strong style={{ fontSize: "12px" }}>{v.label}:</Text><br/>
              {v.keys.map(k => <Tag key={k} color="orange" style={{margin: "2px", fontSize: "11px"}}>{k}</Tag>)}
            </div>
          ))}
        </div>
      ),
      key,
      duration: 2.5,
    });
  };

  const fetchSignatures = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/firmas/${id}`);
      setSignatures(response.data);
    } catch (error) {
      console.error("Error fetching signatures:", error);
    }
  };

  useEffect(() => {
    fetchSignatures();
    if (refresh) refresh();
  }, [id]);

  useEffect(() => {
    // Auto-select new visits if they were just added
    if (visits && visits.length > 0) {
      setSelectedVisits(prev => {
        const next = { ...prev };
        let changed = false;
        
        ["acta", "informe", "anexob"].forEach(docId => {
          const currentSelected = next[docId] || [];
          // If none selected, or if the latest visit is not in selection, add it
          const latestVisitId = visits[visits.length - 1].VisitaID;
          if (!currentSelected.includes(latestVisitId)) {
            next[docId] = [...currentSelected, latestVisitId];
            changed = true;
          }
        });
        
        return changed ? next : prev;
      });
    }
  }, [visits]);

  const getMissingKeys = (fields) => {
    if (!fields) return [];
    return Object.keys(fields).filter(
      (key) => fields[key] === null || fields[key] === "" || fields[key] === undefined
    );
  };

  const docs = [
    {
      id: "solicitud_contrato",
      title: "Solicitud y Contrato",
      generalFields: {
        "Municipio": verification?.Municipio,
        "Estado": verification?.Estado,
        "Número de control interno": verification?.ControlInterno,
        "Fecha de recepción de la solicitud": verification?.FechaRecepcion,
        "Nombre de la persona que firma el contrato": verification?.NombreCompletoPF,
        "Razón social": verification?.NombreCompletoPV,
        "Nombre de la calle": verification?.NombreCalle,
        "Número oficial": verification?.NumeroOficial,
        "Colonia": verification?.ColoniaOAsentamiento,
        "Código postal": verification?.CodigoPostal,
        "Clasificación del SCIAN": verification?.ClasificacionSCIAN,
        "Capacidad de la subestación": verification?.Capacidad,
        "Carga instalada": verification?.CargaInstalada,
        "Alcance de la verificación": verification?.AlcanceVerificacion,
        "Área de proyecto": verification?.AreaProyecto,
        "Objeto de solicitud": verification?.ObjetoSolicitud,
        "Tipo de verificación": verification?.TipoVerificacion,
        "Tipo de instalación": verification?.TipoInstalacion,
        "Monto": verification?.Monto,
        "Tipo de tensión": verification?.TipoTension,
        "Tensión eléctrica de suministro": verification?.TensionElectricaSuministro,
        "Fecha del contrato": verification?.FechaContrato
      },
      requiredSigs: ["Solicitud_UIIE", "Solicitud_Autoriza", "Contrato_UIIE", "Contrato_Firma"],
      isMulti: false
    },
    {
      id: "acta",
      title: "Acta de Evaluación",
      generalFields: {
        "Razón social": verification?.NombreCompletoPV,
        "Clasificación del SCIAN": verification?.ClasificacionSCIAN,
        "Nombre de la calle": verification?.NombreCalle,
        "Número oficial": verification?.NumeroOficial,
        "Colonia": verification?.ColoniaOAsentamiento,
        "Municipio": verification?.Municipio,
        "Código postal": verification?.CodigoPostal,
        "Ciudad": verification?.Ciudad,
        "Estado": verification?.Estado,
        "Móvil (Razón social)": verification?.TelefonoMovilPV_T,
        "Email (Razón social)": verification?.CorreoElectronicoPV_T,
        "Nombre de la persona que atiende la visita": verification?.NombreCompletoPA,
        "Cargo de la persona que atiende la visita": verification?.CargoPA,
      },
      visitFields: (v) => ({
        "Objeto": v.ObjetoVisita, "Fecha": v.FechaVisita, "Inicio": v.HoraInicio, "Término": v.HoraTermino,
        "Circunstancia": v.CircunstanciaVisita, "No Conformidades": v.NoConformidadesEncargado, 
        "Observaciones": v.ObservacionesEncargado, "Acciones": v.Acciones,
        "Atiende (Nombre)": verification?.NombreCompletoPA, "Atiende (Folio)": verification?.FolioPA, "Atiende (Dom)": verification?.DomicilioPA,
        // "Testigo 1 (Nombre)": v.NombreCompletoPT1, "Testigo 1 (Folio)": v.DatoPT1_T, "Testigo 1 (Dom)": v.DomicilioPT1_T,
        // "Testigo 2 (Nombre)": v.NombreCompletoPT2, "Testigo 2 (Folio)": v.DatoPT2_T, "Testigo 2 (Dom)": v.DomicilioPT2_T
      }),
      requiredSigs: ["Acta_UIIE", "Acta_Atiende_visita", "Acta_Testigo_1", "Acta_Testigo_2"],
      isMulti: true,
    },
    {
      id: "informe",
      title: "Informe de Pruebas",
      generalFields: { "Número de control interno": verification?.ControlInterno },
      visitFields: (v) => ({
        "Fecha": v.FechaVisita, 
        "Eq. Aislamiento": v.EqResistenciaAislamiento, "R. Aislamiento": v.ResResistenciaAislamiento, 
        "Eq. Continuidad": v.EqContinuidadElectrica, "C. Eléctrica": v.ResContinuidadElectrica, 
        "Eq. Tierra Física": v.EqResistenciaTierraFisica, "T. Física": v.ResResistenciaTierraFisica,
        "Polaridad": v.ResPolaridadContactos
      }),
      requiredSigs: ["Informe_UIIE"],
      isMulti: true,
    },
    {
      id: "portada",
      title: "Portada",
      generalFields: {
        "Nombre de la calle": verification?.NombreCalle,
        "Número oficial": verification?.NumeroOficial,
        "Colonia": verification?.ColoniaOAsentamiento,
        "Ciudad": verification?.Ciudad,
        "Estado": verification?.Estado,
        "Código postal": verification?.CodigoPostal,
        "Aplica NOM 007": verification?.Nom007,
        "Aplica NOM 013": verification?.Nom013,
      },
      requiredSigs: ["Portada_UIIE"],
      isMulti: false
    },
    {
      id: "anexob",
      title: "Anexo B",
      generalFields: {
        "Número de control interno": verification?.ControlInterno,
      },
      visitFields: (v) => ({ "Fecha": v.FechaVisita }),
      requiredSigs: ["AnexoB_UIIE"],
      isMulti: true,
    },
    {
      id: "supervision",
      title: "Supervisión",
      generalFields: {
        "Número de control interno": verification?.ControlInterno,
        "Fecha del contrato": verification?.FechaContrato,
      },
      requiredSigs: ["Supervision_Firma"],
      isMulti: false
    }
  ];

  const processedDocs = docs.map(doc => {
    let total = 0, filled = 0;
    const mGen = getMissingKeys(doc.generalFields);
    total += Object.keys(doc.generalFields).length;
    filled += (total - mGen.length);

    let mVisits = [];
    if (doc.isMulti) {
      // Logic: If no visits exist, count fields for 1 theoretical visit as missing.
      // If visits exist, count fields for ALL of them.
      const visitsToCount = visits.length > 0 ? visits : [null]; 

      visitsToCount.forEach((v, idx) => {
        // Use doc.visitFields if defined, otherwise default to a basic check for Fecha
        const vFields = (doc.visitFields && v) ? doc.visitFields(v) : (doc.visitFields ? doc.visitFields({}) : { "Fecha": null });
        const m = getMissingKeys(vFields);
        
        total += Object.keys(vFields).length;
        filled += (Object.keys(vFields).length - m.length);
        
        if (m.length > 0) {
          const label = v ? `Visita ${moment.utc(v.FechaVisita).format('DD/MM')}` : "Visita (Pendiente)";
          mVisits.push({ label, keys: m });
        }
      });
    }

    const mSigs = doc.requiredSigs.filter(sName => !sName.includes("UIIE") && sName !== "Supervision_Firma" && !signatures.some(s => s.NombreFormato === sName));
    const percent = total > 0 ? Math.round((filled / total) * 100) : 0;
    
    return { ...doc, mGen, mVisits, mSigs, percent, totalFields: total, filledFields: filled, isReady: percent === 100 && mSigs.length === 0 };
  });

  const handleBatchAction = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    
    const selection = selectedIds.map(docId => ({
      templateName: docId,
      visitIds: selectedVisits[docId] || []
    }));

    try {
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/formatos/batch`,
        data: { selection, id },
        responseType: "blob",
      });

      let fileName = `PRODUCCION_${Date.now()}.zip`;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename="?([^"]+)"?/.exec(disposition);
        if (matches && matches[1]) fileName = matches[1];
      }

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.download = fileName;
      link.click();
      message.success("Lote generado correctamente");
    } catch (e) {
      console.error(e);
      message.error("Error al generar el lote");
    } finally {
      setLoading(false);
    }
  };

  const handleSingleDownload = async (docId) => {
    setLoading(true);
    const selectedVisitIds = selectedVisits[docId] || [];
    
    try {
      let url = "";
      let method = "get";
      let data = null;

      if (selectedVisitIds.length > 1) {
        // More than one visit selected: use batch to get a ZIP
        url = `${process.env.REACT_APP_API_URL}/formatos/batch`;
        method = "post";
        data = { 
          selection: [{ templateName: docId, visitIds: selectedVisitIds }], 
          id 
        };
      } else if (selectedVisitIds.length === 1) {
        // Exactly one visit selected: use specific visit route for single DOCX
        url = `${process.env.REACT_APP_API_URL}/formatos/${docId}/visita/${selectedVisitIds[0]}`;
      } else {
        // No visits (general template): use standard ID route for single DOCX
        url = `${process.env.REACT_APP_API_URL}/formatos/${docId}/${id}`;
      }

      const response = await axios({
        method,
        url,
        data,
        responseType: "blob",
      });

      // Try to get filename from header
      let fileName = `${docId.toUpperCase()}_${Date.now()}.docx`;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename="?([^"]+)"?/.exec(disposition);
        if (matches && matches[1]) fileName = matches[1];
      }

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.download = fileName;
      link.click();
      message.success("Descarga iniciada");
    } catch (e) { 
      console.error(e);
      message.error("Error al descargar el documento"); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* ── Header card ── */}
      <div style={{ ...CARD, padding: 0 }}>
        {/* Title row */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "18px 20px 14px",
          borderBottom: "0.5px solid rgba(0,0,0,0.06)",
          flexWrap: "wrap",
        }}>
          {/* Icon badge */}
          <div style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: "rgba(24,144,255,0.08)",
            border: "0.5px solid rgba(24,144,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <FileDoneOutlined style={{ fontSize: 17, color: "#1677ff" }} />
          </div>

          {/* Title + subtitle */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(0,0,0,0.88)", lineHeight: 1.3 }}>
              Producción de Entregables
            </div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.38)", marginTop: 1 }}>
              Gestión unificada de documentos y visitas para descarga masiva
            </div>
          </div>

          {/* Actions */}
          <Space size={8}>
            {selectedIds.length > 0 && (
              <Button onClick={() => setSelectedIds([])} style={{ fontSize: 13 }}>
                Limpiar ({selectedIds.length})
              </Button>
            )}
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              loading={loading}
              disabled={selectedIds.length === 0}
              onClick={handleBatchAction}
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              Descargar Selección
            </Button>
          </Space>
        </div>
      </div>

      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        {processedDocs.map((doc) => (
          <Card 
            key={doc.id}
            bodyStyle={{ padding: 0 }}
            style={{ 
              borderRadius: "16px", overflow: "hidden", 
              border: selectedIds.includes(doc.id) ? "2px solid #1890ff" : "1px solid #f0f0f0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
            }}
          >
            <Row>
              <Col span={1} style={{ background: "#fafafa", borderRight: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Tooltip title={!doc.isReady ? "Complete todos los campos y firmas para habilitar" : ""}>
                  <Checkbox 
                    disabled={!doc.isReady}
                    checked={selectedIds.includes(doc.id)} 
                    onChange={() => setSelectedIds(prev => prev.includes(doc.id) ? prev.filter(i => i !== doc.id) : [...prev, doc.id])} 
                  />
                </Tooltip>
              </Col>

              <Col xs={22} sm={23} style={{ padding: "20px" }}>
                <Row gutter={[24, 24]} align="middle">
                  {/* DOC INFO */}
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <Space align="start" size="middle">
                      <div style={{ 
                        width: "44px", height: "44px", borderRadius: "8px", 
                        background: doc.isReady ? "#f0f5ff" : "#fff1f0",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: doc.isReady ? "1px solid #adc6ff" : "1px solid #ffa39e",
                        flexShrink: 0
                      }}>
                        <FileWordFilled style={{ fontSize: "22px", color: doc.isReady ? "#2b579a" : "#cf1322" }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "12px", color: doc.isReady ? "#262626" : "#cf1322", lineHeight: "1.2", marginBottom: "4px" }}>{doc.title}</div>
                        {doc.isMulti && (
                          <div style={{ marginTop: "6px" }}>
                            <Text strong style={{ fontSize: "9px", color: "#8c8c8c", display: "block", marginBottom: "2px" }}>
                              <CalendarOutlined /> VISITAS:
                            </Text>
                            {visits.length > 0 ? (
                              <Checkbox.Group 
                                value={selectedVisits[doc.id] || []}
                                onChange={(vals) => setSelectedVisits(prev => ({ ...prev, [doc.id]: vals }))}
                              >
                                <Space direction="horizontal" size={8} wrap>
                                  {visits.map(v => (
                                    <Checkbox key={v.VisitaID} value={v.VisitaID} style={{ fontSize: "10px" }}>
                                      {moment.utc(v.FechaVisita).format('DD/MM')}
                                    </Checkbox>
                                  ))}
                                </Space>
                              </Checkbox.Group>
                            ) : (
                              <Text type="danger" style={{ fontSize: "10px" }}>Sin visitas</Text>
                            )}
                          </div>
                        )}
                      </div>
                    </Space>
                  </Col>

                  {/* INTEGRITY */}
                  <Col xs={24} sm={12} md={12} lg={5}>
                    <div style={{ padding: "0 4px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <Text style={{ fontSize: "11px", color: "#595959" }}>Integridad</Text>
                        <Text type={doc.isReady ? "success" : "warning"} strong style={{ fontSize: "11px" }}>{doc.filledFields} / {doc.totalFields}</Text>
                      </div>
                      <Progress percent={doc.percent} showInfo={false} strokeColor={doc.isReady ? "#52c41a" : "#faad14"} strokeWidth={8} />
                      {(doc.mGen.length > 0 || doc.mVisits.length > 0) && (
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<InfoCircleOutlined />} 
                          onClick={() => showMissingFields(doc)}
                          style={{ padding: 0, marginTop: "4px", fontSize: "10px" }}
                        >
                          Ver faltantes
                        </Button>
                      )}
                    </div>
                  </Col>

                  {/* SIGNATURES */}
                  <Col xs={24} sm={24} md={24} lg={9}>
                    <div style={{ borderLeft: "1px solid #f0f0f0", paddingLeft: "20px", marginLeft: "-20px" }}>
                      <Text strong style={{ display: "block", marginBottom: "8px", fontSize: "10px", color: "#8c8c8c", textTransform: 'uppercase' }}>
                        <HighlightOutlined /> Firmas Requeridas
                      </Text>
                      {doc.requiredSigs.length > 0 ? (
                        <Space wrap size={[16, 16]}>
                          {doc.requiredSigs.map(sigName => {
                            const exists = signatures.find(s => s.NombreFormato === sigName);
                            const isInternal = sigName.includes("UIIE") || sigName === "Supervision_Firma";
                            return (
                              <Tooltip key={sigName} title={isInternal ? "Firma no requerida" : ""}>
                                <div style={{ 
                                  border: (exists || isInternal) ? "1px solid #f0f0f0" : "1px solid #ffa39e", 
                                  borderRadius: "10px", padding: "8px", 
                                  background: (exists || isInternal) ? "#fafafa" : "#fff1f0",
                                  display: "flex", flexDirection: "column", alignItems: "center", minWidth: "110px", position: "relative"
                                }}>
                                  <Text ellipsis style={{ fontSize: "9px", fontWeight: 700, marginBottom: "6px", color: "#595959", width: "100px", textAlign: "center", textTransform: 'uppercase' }}>
                                    {getSignatureLabel(sigName)}
                                  </Text>
                                  {exists ? (
                                    <div style={{ position: "relative" }}>
                                      <Image width={90} height={45} src={exists.Ruta} 
                                        style={{ objectFit: "contain", background: "#fff", borderRadius: "6px", border: "1px solid #eee" }}
                                        preview={{ mask: <EyeOutlined style={{fontSize: "16px"}} /> }}
                                      />
                                      <Button size="small" type="primary" shape="circle" icon={<EditOutlined style={{fontSize: "14px"}} />} 
                                        onClick={() => { setActiveSignature(sigName); setIsModalVisible(true); }}
                                        disabled={isInternal}
                                        style={{ position: "absolute", top: -12, right: -12, width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 2 }}
                                      />
                                    </div>
                                  ) : (
                                    <Button size="large" type="dashed" onClick={() => { setActiveSignature(sigName); setIsModalVisible(true); }}
                                      icon={<EditOutlined />}
                                      danger={!isInternal}
                                      disabled={isInternal}
                                      style={{ width: "90px", height: "45px", fontSize: "13px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px" }}>
                                      {isInternal ? "Interna" : "Firmar"}
                                    </Button>
                                  )}
                                </div>
                              </Tooltip>
                            );
                          })}
                        </Space>
                      ) : (
                        <Text type="secondary" style={{ fontStyle: "italic", fontSize: "11px" }}>No requiere firmas adicionales</Text>
                      )}
                    </div>
                  </Col>

                  {/* ACTIONS */}
                  <Col xs={24} sm={24} md={24} lg={4} style={{ textAlign: "right" }}>
                    <Button 
                      type="primary" shape="round" size="large" icon={<DownloadOutlined />} 
                      onClick={(e) => { e.stopPropagation(); handleSingleDownload(doc.id); }}
                      disabled={!doc.isReady}
                      block
                    >
                      Generar
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>

      <SignatureModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSave={async (dataURL, verifId, formName) => {
          setIsModalVisible(false);
          const blob = await (await fetch(dataURL)).blob();
          const file = new File([blob], `${verifId}_${formName}.png`, { type: "image/png" });
          const formData = new FormData();
          formData.append("signature", file);
          formData.append("verificacionid", verifId);
          formData.append("plantilla", formName);
          formData.append("UsuarioID", 1);
          try {
            await axios.post(`${process.env.REACT_APP_API_URL}/firmas`, formData, { headers: { "Content-Type": "multipart/form-data" } });
            message.success("Firma actualizada");
            fetchSignatures();
            if (refresh) refresh();
          } catch (e) { message.error("Error al guardar"); }
        }}
        verificacionId={id}
        nombreFormato={activeSignature}
      />
    </div>
  );
}
